import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import Select2Multiple from 'v-select2-multiple-component';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import ValidationService from '@/services/ValidationService';
import CONFIG from "@/assets/js/config.custom.js";
import upLoad from '@/assets/img/upload_photo3.jpg';

export default {
    name: 'enrollmentInvoiceCreate',
    components: {
        MainLayout,
        Loading,
        Select2Multiple,
        flatPickr,
    },
    mixins: [ValidationService],
    data() {
        return {
            CONFIG,
            isLoading: false,
            studentInfo: {},
			discountTypeId: null,
            discountTypes: [],
            discountLabels: [],
            discountLimit: null,
            paymentItemId: null,
            itemQuantity: 1,
            paymentItemTypes: [],
			paymentItemLabels: [],
			hasDiscountError: false,
            hasItemError: false,
            disabled: false,
            enrollmentPaymentItems: [],
            quantity: 1,
            tuitionFeeAmount: 0,
            discountItems: [],
            grossTotal: 0,
            taxAmount: 0,
            netTotalAmount: 0,
            grandTotal: 0,
            imageLargeMsg: CONFIG.IMAGE_TOO_LARGE,
            ssImage: '',
            upLoad,
            paymentMethodsArr: [],
            paymentHistories: [],
            showPaymentTemplate: false,
            paymentStatusOptions: CONFIG.PAYMENT_STATUSES_OPTIONS.filter(o => o.id === CONFIG.PAYMENT_STATUS.STATUS_APPROVED || o.id === CONFIG.PAYMENT_STATUS.STATUS_ADMIN_REQUEST),
            firstPaymentDueDate: null,
            issueDate: new Date().toISOString().slice(0, 10),
            taxRate: 0,
            note: "",
        };
    },

    watch: {
        grossTotal() {
            this.calculateTotal();
        },
        taxRate() {
            this.calculateTotal();
        },
        discountTypeId(value) {
            if (value !== null && value !== undefined && value !== '') {
                this.hasDiscountError = false;
                this.clearValidationError('discount_type_id');
            }
        }
    },

    computed: {
        isZeroGrandTotal() {
            return Number(this.grandTotal) === 0;
        },
    },

    mounted() {
        this.isLoading = true;
        this.getStudentInfo();
        this.getFirstTuitionFee(this.$route.params.tuitionPaymentId);
		this.getDiscountTypes();
        this.getPaymentItems();
        this.getDefaultPaymentItems();
        this.getPaymentMethods();
        this.getTaxRate();
        this.initPaymentHistories();
        this.checkTuitionPayment();
    },

    methods: {
        /**
         * Normalize any user-provided value into a safe positive integer for quantity-like fields.
         *
         * @param {string|number|null|undefined} value Raw value from text/number inputs.
         * @returns {number} Sanitized integer greater than or equal to 1.
         * @example
         * this.sanitizePositiveInteger('3.8')
         * @since enrollment-invoice-multipayment
         * @see sanitizeItemQuantity
         */
        sanitizePositiveInteger(value) {
            const matched = String(value ?? '').match(/-?\d+(\.\d+)?/);
            if (!matched) return 1;
            const parsed = Math.floor(Math.abs(Number(matched[0])));
            return isNaN(parsed) || parsed <= 0 ? 1 : parsed;
        },

        /**
         * Sanitize the standalone invoice item quantity input before adding an item row.
         *
         * @returns {void} Does not return a value; mutates `this.itemQuantity`.
         * @example
         * this.itemQuantity = '02';
         * this.sanitizeItemQuantity();
         * @since enrollment-invoice-multipayment
         * @see sanitizePositiveInteger
         */
        sanitizeItemQuantity() {
            this.itemQuantity = this.sanitizePositiveInteger(this.itemQuantity);
        },

        /**
         * Reserved initializer for payment history templates during component setup.
         *
         * @returns {void} Currently no-op.
         * @example
         * this.initPaymentHistories();
         * @since enrollment-invoice-multipayment
         * @deprecated Use addPaymentHistory/createPaymentHistory flow for runtime initialization.
         * @see addPaymentHistory
         */
        initPaymentHistories() {
            return;
        },

        /**
         * Build a new payment history DTO used by multi-payment submission payloads.
         *
         * @param {number|null} [amount=null] Initial amount for the history row.
         * @returns {{amount:number|null,payment_method_id:number|null,status:number,remark:string,ss_image:string,receiver:string}} New payment history object.
         * @example
         * const history = this.createPaymentHistory(50000);
         * @since enrollment-invoice-multipayment
         * @see addPaymentHistory
         */
        createPaymentHistory(amount = null) {
            const receiver = this.$store?.state?.user?.name || '';
            return {
                amount,
                payment_method_id: null,
                status: CONFIG.PAYMENT_STATUS.STATUS_APPROVED,
                remark: '',
                ss_image: '',
                receiver,
            };
        },

        checkTuitionPayment() {
        const tuitionPaymentId = this.$route.params.tuitionPaymentId;

        if (!tuitionPaymentId || tuitionPaymentId === 'undefined' || !Number(tuitionPaymentId)) {
            return;
        }

        axios.get(`enrollment/first-tuition-fee/${tuitionPaymentId}`)
            .then(response => {
                const firstPayment = response.data.first_tuition_fee;
                
                if (firstPayment && firstPayment.status === CONFIG.PAYMENT_STATUS.STATUS_APPROVED) {
                    
                    this.$notification.error('Payment has already been created. Redirecting to statement detail.', {
                        timer: 3,
                        position: "bottomCenter",
                    });

                    this.$router.push({ name: 'studentStatementDetail', params: { tuitionPaymentId } });
                }
            })
            .catch((error) => {
                console.error('Check tuition payment error:', error);
            });
        },


        /**
         * Append a new payment history row and ensure the payment template section is visible.
         *
         * @returns {void} Does not return a value; mutates UI state and `paymentHistories`.
         * @example
         * this.addPaymentHistory();
         * @since enrollment-invoice-multipayment
         * @see createPaymentHistory
         */
        addPaymentHistory() {
            this.showPaymentTemplate = true;
            this.paymentHistories.push(this.createPaymentHistory(null));
        },

        /**
         * Remove one payment history row by index and hide payment template when no rows remain.
         *
         * @param {number} historyIndex Zero-based index of the payment history to remove.
         * @returns {void} Does not return a value; mutates `paymentHistories` and visibility state.
         * @example
         * this.removePaymentHistory(0);
         * @since enrollment-invoice-multipayment
         * @see addPaymentHistory
         */
        removePaymentHistory(historyIndex) {
            this.paymentHistories.splice(historyIndex, 1);
            this.clearValidationErrors();
            if (this.paymentHistories.length === 0) {
                this.showPaymentTemplate = false;
            }
        },

        /**
         * Get Tax Rate
         */
        getTaxRate() {
            axios.get(`setting/get-tax-rate`)
            .then(res => {
                this.taxRate = res.data.tax_rate;
            })
            .catch(err => {
                console.log(err);
                this.taxRate = CONFIG.TAX_RATE;
            });
        },

        /**
         * Show student list (for breadcrumb)
         */
        showStudentList() {
            this.$router.push({
                path: "/student/list"
            });
        },

        /**
         * Show student profile (for breadcrumb)
         */
        showProfile() {
            this.$router.push({
                name: "studentProfile",
                params: {
                    leavedStatus: "active",
                    id: this.$route.params.classStudentId,
                }
            });
        },

        redirectStatementDetailPage() {
            this.$router.push({
                name: "studentStatementDetail",
                params: {
                    tuitionPaymentId: this.$route.params.tuitionPaymentId,
                }
            });
        },

        /**
         * Show Tuition Plan page (for breadcrumb)
         */
        showTuitionPlan() {
            this.$router.push({
                name: "enrollmentPaymentCreate",
                params: {
                    classStudentId:  this.$route.params.classStudentId,
                },
                query: {
                    tuitionPaymentId: this.$route.params.tuitionPaymentId
                }
            });
        },

        /**
         * Get student info
         */
        getStudentInfo() {
            axios.get(`enrollment/student/info-for-tuition/${this.$route.params.tuitionPaymentId}`)
                .then(response => {
                    this.studentInfo = response.data;
                })
                .catch(error => {
                    console.error("Error fetching student info:", error);
                });
        },

        /**
         * Get first tuition fee
         * @param {int} tuitionPaymentId 
         */
        getFirstTuitionFee(tuitionPaymentId) {
            axios.get(`enrollment/first-tuition-fee/${tuitionPaymentId}`)
                .then(response => {
                    this.tuitionFeeAmount = response.data.first_tuition_fee?.net_total_amt || 0;
                    this.firstPaymentDueDate = response.data.first_tuition_fee?.due_date || null;
                    this.getDefaultPaymentItems();
                })
                .catch(error => {
                    console.error("Error fetching tuition fee:", error);
                });
        },

        /**
         * Get discount types
         */
        getDiscountTypes() {
			axios.get(`enrollment/discounts`)
                .then(response => {
                   this.discountTypes = response.data.discount_types;
                   this.discountLabels = response.data.discount_labels;
                   const parsedDiscountLimit = Number(response.data.discount_limit);
                   this.discountLimit = Number.isInteger(parsedDiscountLimit) ? parsedDiscountLimit : null;
                })
                .catch(error => {
                    console.error("Error fetching discount types:", error);
                });
        },

        /**
         * Clear one validation error key if present.
         *
         * @param {string} fieldName
         * @returns {void}
         */
        clearValidationError(fieldName) {
            if (!fieldName || !this.validationErrors || !Object.prototype.hasOwnProperty.call(this.validationErrors, fieldName)) {
                return;
            }

            this.$delete(this.validationErrors, fieldName);
        },

        /**
         * Set one validation error key with a single message.
         *
         * @param {string} fieldName
         * @param {string} message
         * @returns {void}
         */
        setValidationError(fieldName, message) {
            if (!fieldName || !message) {
                return;
            }

            this.$set(this.validationErrors, fieldName, [message]);
        },

        /**
         * get payment items
         */
        getPaymentItems() {
            axios.get('enrollment/get-payment-items')
                .then(res => {
                    this.paymentItemLabels = res.data.payment_items;
                }).catch((error) => {
                    if (error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }
                });
        },

        /**
         * Get  default enrollment payment items
         */
        getDefaultPaymentItems() {
            axios.get('enrollment/get-default-payment-items')
                .then(res => {
                    this.enrollmentPaymentItems = res.data.default_payment_items || [];
                    this.enrollmentPaymentItems.forEach(item => {
                        if (item.is_tuition_fee) {
                            item.item_amount = this.tuitionFeeAmount;
                        }
                        const qty = Number(item.quantity);
                        item.quantity = isNaN(qty) || qty <= 0 ? 1 : qty;
                    });
                    this.calculateGrossTotal();
                }).catch((error) => {
                    if (error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }
                });
        },

        /**
         * Add payment item
         * @param {int} paymentItemId 
         * @param {int} itemQuantity 
         * @returns 
         */
        addPaymentItem(paymentItemId, itemQuantity) {
            if (paymentItemId == null) {
				this.hasItemError = true;
				return;
			}
            this.hasItemError = false;
			const paymentItem = this.paymentItemLabels.find(item => item.id === Number(paymentItemId));
            if (!paymentItem) {
                return;
            } else {
                if (paymentItem.is_ferry_fee) {
                    itemQuantity = 1;
                }
            }
            const sanitizedQty = this.sanitizePositiveInteger(itemQuantity);
            // Prevent duplicate rows: if item exists, update quantity instead of pushing
            const existing = this.enrollmentPaymentItems.find(it => it.id === paymentItem.id);
            if (existing) {
                existing.quantity = sanitizedQty;
            } else {
                const clone = { ...paymentItem, quantity: sanitizedQty };
                this.enrollmentPaymentItems.push(clone);
            }
            this.calculateGrossTotal();
        },

        /**
         * Remove payment item
         * @param {int} paymentItemId 
         */
        removeItem(paymentItemId) {
            this.enrollmentPaymentItems = this.enrollmentPaymentItems.filter(item => item.id !== Number(paymentItemId));
            this.hasItemError = false;
            this.clearValidationErrors();
            this.calculateGrossTotal();
        },

        /**
         * Add discount
         * @param {int} discountTypeId 
         * @returns 
         */
		addDiscount(discountTypeId) {
            this.clearValidationError('discount_type_id');

			if (discountTypeId == null || discountTypeId === '') {
				this.hasDiscountError = true;
				return;
			}
            this.hasDiscountError = false;
			const discount = this.discountTypes.find(item => item.id === Number(discountTypeId));
            if (!discount) {
                return;
            }
            // Prevent duplicate discount rows and discount limit exceeding
            const exists = this.discountItems.some(d => d.id === discount.id);
            if (exists) {
                this.setValidationError('discount_type_id', 'Discount type already added.');
                return;
            }

            const selectedDiscountCount = this.discountItems.filter(item => item?.id).length;
            if (Number.isInteger(this.discountLimit) && this.discountLimit >= 0 && selectedDiscountCount >= this.discountLimit) {
                const discountLabel = this.discountLimit === 1 ? 'discount' : 'discounts';
                this.setValidationError('discount_items', `You can select up to ${this.discountLimit} ${discountLabel}.`);
                return;
            }

            this.clearValidationError('discount_items');
            const clone = { ...discount };
            this.discountItems.push(clone);
            this.calculateTotal();
		},

        /**
         * Calculate discount amount based on discount_type
         * @param {object} discount 
         * @returns 
         */
        calculateDiscountAmount(discount) {
            if (discount.discount_type === CONFIG.DISCOUNT_TYPE.PERCENTAGE) {
                return Math.round((discount.discount_amount / 100) * this.grossTotal);
            } else if (discount.discount_type === CONFIG.DISCOUNT_TYPE.FIXED) {
                return discount.discount_amount;
            }
            return 0;
        },

        /**
         * Format amount with comma separation and no decimals.
         * @param {Number|String} value
         * @returns {String}
         */
        formatAmount(value) {
            if (value === null || value === undefined) return '0';
            const num = Number(String(value).replace(/,/g, ''));
            if (Number.isNaN(num)) return value;
            return Math.round(num).toLocaleString('en-US');
        },

        /**
         * Remove discount
         * @param {int} discountId 
         */
        removeDiscount(discountId) {
            this.clearValidationError('discount_type_id');
            this.clearValidationError('discount_items');
            this.discountItems = this.discountItems.filter(item => item.id !== Number(discountId));
            this.hasDiscountError = false;
            this.clearValidationErrors();
            this.calculateTotal();
        },

        /**
         * Calculate total amount
         */
        calculateGrossTotal() {
            let grossTotalAmount = 0;
            this.enrollmentPaymentItems.forEach(item => {
                const safeQty = this.sanitizePositiveInteger(item.quantity);
                item.quantity = safeQty;
                grossTotalAmount += Number(item.item_amount) * safeQty;
            });
            this.grossTotal = grossTotalAmount;
            this.isLoading = false;
        },

        /**
         * Calculate tax and final total amount 
         * tax = gross total * tax_rate
         * final total = gross total + tax
         */
        calculateTotal() {
            let netTotalAmount = this.grossTotal;
            this.discountItems.forEach(item => {
                netTotalAmount -= this.calculateDiscountAmount(item);
            });
            netTotalAmount = Math.max(netTotalAmount, 0);
            this.netTotalAmount = netTotalAmount;
            this.taxAmount = Math.round(netTotalAmount * this.taxRate);
            this.grandTotal = netTotalAmount + this.taxAmount;
            this.syncZeroGrandTotalPaymentHistory();
        },

        /**
         * Get payment methods
         */
        getPaymentMethods() {
            axios.get('getPaymentMethod')
                .then(res => {
                    this.paymentMethodsArr = res.data || [];
                })
                .catch((error) => {
                    if (error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    }
                });
        },

        getCashPaymentMethodId() {
            const list = Array.isArray(this.paymentMethodsArr) ? this.paymentMethodsArr : [];
            const cash = list.find(m => String(m?.text ?? m?.payment_method ?? '').toLowerCase().includes('cash'));
            const id = cash?.id ?? list?.[0]?.id ?? null;
            return id != null ? Number(id) : null;
        },

        syncZeroGrandTotalPaymentHistory() {
            const isZero = Number(this.grandTotal) === 0;
            const histories = Array.isArray(this.paymentHistories) ? this.paymentHistories : [];

            if (isZero) {
                const payMethodId = this.getCashPaymentMethodId();
                const existingAuto = histories.find(h => h?._auto_zero_grand_total);
                if (existingAuto) {
                    existingAuto.amount = 0;
                    if (existingAuto.payment_method_id == null) {
                        existingAuto.payment_method_id = payMethodId;
                    }
                    this.showPaymentTemplate = true;
                    return;
                }
                if (histories.length > 0) {
                    return;
                }
                const history = this.createPaymentHistory(0);
                history.amount = 0;
                history.payment_method_id = payMethodId;
                history._auto_zero_grand_total = true;
                this.showPaymentTemplate = true;
                this.paymentHistories.push(history);
                return;
            }

            if (histories.length === 0) {
                return;
            }

            const next = histories.filter(h => !h?._auto_zero_grand_total);
            if (next.length !== histories.length) {
                this.paymentHistories = next;
                if (this.paymentHistories.length === 0) {
                    this.showPaymentTemplate = false;
                }
            }
        },

        /**
         * Image upload
         * @param {object} e 
         * @returns 
         */
        imgUpload(e, historyIndex = null) {
            let files = e.target.files || e.dataTransfer.files;
            if (!files.length) {
                return null;
            } else {
                if (e.target.files[0].type.includes("image/")) {
                    if (e.target.files[0].type == "image/gif") {
                        this.$notification.error("File not supported!", {
                        timer: 3,
                        position: "bottomCenter",
                        });
                        return null;
                    }
                    let reader = new FileReader();
                    reader.onload = (e) => {
                        if (e.target.result.length > 5242880) {
                            this.$notification.error(this.imageLargeMsg, {
                                timer: 3,
                                position: "bottomCenter",
                            });
                        } else {
                            if (historyIndex !== null && this.paymentHistories[historyIndex]) {
                                this.paymentHistories[historyIndex].ss_image = e.target.result;
                                this.clearValidationError('payment_histories.' + historyIndex + '.ss_image');
                            } else {
                                this.ssImage = e.target.result;
                            }
                            if (e?.target) {
                                e.target.value = '';
                            }
                        }
                    }
                    reader.readAsDataURL(files[0]);
                } else {
                    this.$notification.error("File not supported!", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                return null;
                }
            }
        },

        /**
         * Remove image
         */
        removeImg() {
            this.ssImage = '';
        },
        /**
         * remove image
         * @param {int} historyIndex 
         */
        removeHistoryImg(historyIndex) {
            if (this.paymentHistories[historyIndex]) {
                this.paymentHistories[historyIndex].ss_image = '';
                this.clearValidationError('payment_histories.' + historyIndex + '.ss_image');
            }
        },

        /**
         * Save draft
         */
        saveDraft() {
            this.clearValidationErrors();
            this.disabled = true;
            axios.post(`enrollment/payment/save-draft/${this.$route.params.classStudentId}/${this.$route.params.tuitionPaymentId}`, {
                payment_items: this.enrollmentPaymentItems,
                discount_items: this.discountItems,
                payment_histories: this.paymentHistories,
                note: this.note,
            })
            .then(response => {
                this.disabled = false;
                if (response.data.success == true) {
                    this.$notification.success("Enrollment payment is successfully saved as draft", {
                        timer: 3,
                        position: "bottomCenter"
                    });
                   this.redirectStatementDetailPage();
                }
            })
            .catch(error => {
                if (error.response.status == 422 && error.response?.data?.error) {
                    this.$notification.error(error.response.data.error, {
                        timer: 3,
                        position: "bottomCenter",
                    });
                }

                this.disabled = false;
                this.printValidationError(error.response);
                console.error("Error fetching payment plan:", error);
                this.isLoading = false;
            });
        },

        /**
         * Make payment
         */
        makePayment() {
            this.validationErrors = {};
            if (!Array.isArray(this.paymentHistories) || this.paymentHistories.length === 0) {
                this.$notification.error("Make Payment requires at least one payment history", {
                    timer: 3,
                    position: "bottomCenter"
                });
                return;
            }

            // Require screenshot for approved histories
            for (let i = 0; i < this.paymentHistories.length; i++) {
                const history = this.paymentHistories[i];
                if (parseInt(history.status) === CONFIG.PAYMENT_STATUS.STATUS_APPROVED) {
                    if (!history.ss_image || history.ss_image.trim() === '') {
                        this.setValidationError('payment_histories.' + i + '.ss_image', 'Payment Screenshot is required.');
                        return;
                    }
                }
            }

            // Force payment_method_id to null for non-approved statuses
            const sanitizedHistories = this.paymentHistories.map(history => {
                if (history.status !== CONFIG.PAYMENT_STATUS.STATUS_APPROVED) {
                    return { ...history, payment_method_id: null };
                }
                return history;
            });

            this.disabled = true;
            axios.post(`enrollment/payment/make-payment/${this.$route.params.classStudentId}/${this.$route.params.tuitionPaymentId}`, {
                payment_items: this.enrollmentPaymentItems,
                discount_items: this.discountItems,
                payment_histories: sanitizedHistories,
                note: this.note,
            })
            .then(response => {
                this.disabled = false;
                if (response.data.success == true) {
                    this.$notification.success("Enrollment payment is successfully created", {
                        timer: 3,
                        position: "bottomCenter"
                    });
                   this.redirectStatementDetailPage();
                }
            })
            .catch(error => {
                if (error.response.status == 422 && error.response?.data?.error) {
                    this.$notification.error(error.response.data.error, {
                        timer: 3,
                        position: "bottomCenter",
                    });
                }
                
                this.disabled = false;
                this.printValidationError(error.response);
                console.error("Error fetching payment plan:", error);
                this.isLoading = false;
            });
        }
    },
};
